import { useState, useEffect, useCallback } from 'react';
import { 
  FileText, Menu, Image, Settings, Plus, Edit2, Trash2, Eye, 
  GripVertical, ChevronRight, ChevronDown, ExternalLink, Save,
  ArrowLeft, Globe, Search, MoreVertical, Copy, Home, Link2,
  Monitor, Tablet, Smartphone, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import WYSIWYGEditor from './WYSIWYGEditor';
import NavigationManager from './NavigationManager';
import SimpleMediaLibrary from './SimpleMediaLibrary';
import { api } from '@/utils/apiClient';

export default function SimpleCMS() {
  const [activeSection, setActiveSection] = useState('pages');
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPage, setEditingPage] = useState(null);
  const [showNewPageDialog, setShowNewPageDialog] = useState(false);
  const [newPageTitle, setNewPageTitle] = useState('');
  const [newPageSlug, setNewPageSlug] = useState('');

  // Fetch all pages
  const fetchPages = useCallback(async () => {
    try {
      const res = await api.get('/admin/cms/simple/pages');
      setPages(res.data || []);
    } catch (err) {
      console.error('Failed to fetch pages:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchPages();
  }, [fetchPages]);

  // Create new page
  const createPage = async () => {
    if (!newPageTitle.trim()) {
      toast.error('Please enter a page title');
      return;
    }
    
    const slug = newPageSlug.trim() || newPageTitle.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    
    try {
      const res = await api.post('/admin/cms/simple/pages', {
        title: newPageTitle,
        slug: slug,
        content: '',
        status: 'draft'
      });
      
      toast.success('Page created!');
      setShowNewPageDialog(false);
      setNewPageTitle('');
      setNewPageSlug('');
      fetchPages();
      setEditingPage(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create page');
    }
  };

  // Delete page
  const deletePage = async (slug) => {
    if (!confirm('Are you sure you want to delete this page?')) return;
    
    try {
      await api.delete(`/admin/cms/simple/pages/${slug}`);
      toast.success('Page deleted');
      fetchPages();
      if (editingPage?.slug === slug) {
        setEditingPage(null);
      }
    } catch (err) {
      toast.error('Failed to delete page');
    }
  };

  // Duplicate page
  const duplicatePage = async (page) => {
    try {
      await api.post('/admin/cms/simple/pages', {
        title: `${page.title} (Copy)`,
        slug: `${page.slug}-copy`,
        content: page.content,
        featured_image: page.featured_image,
        status: 'draft'
      });
      
      toast.success('Page duplicated!');
      fetchPages();
    } catch (err) {
      toast.error('Failed to duplicate page');
    }
  };

  // Render sidebar
  const renderSidebar = () => (
    <div className="w-64 bg-[#1e1e1e] text-white h-full flex flex-col">
      {/* Logo */}
      <div className="p-4 border-b border-gray-700">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-primary rounded flex items-center justify-center">
            <span className="text-white font-bold text-sm">B</span>
          </div>
          <div>
            <p className="font-medium text-sm">Bulaccino</p>
            <p className="text-xs text-gray-400">Site Manager</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-2">
        <button
          onClick={() => { setActiveSection('pages'); setEditingPage(null); }}
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
            activeSection === 'pages' ? 'bg-primary text-white' : 'text-gray-300 hover:bg-gray-700'
          }`}
        >
          <FileText className="w-5 h-5" />
          <span>Pages</span>
        </button>
        
        <button
          onClick={() => { setActiveSection('navigation'); setEditingPage(null); }}
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
            activeSection === 'navigation' ? 'bg-primary text-white' : 'text-gray-300 hover:bg-gray-700'
          }`}
        >
          <Menu className="w-5 h-5" />
          <span>Navigation</span>
        </button>
        
        <button
          onClick={() => { setActiveSection('media'); setEditingPage(null); }}
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
            activeSection === 'media' ? 'bg-primary text-white' : 'text-gray-300 hover:bg-gray-700'
          }`}
        >
          <Image className="w-5 h-5" />
          <span>Media</span>
        </button>
      </nav>

      {/* View Site Link */}
      <div className="p-4 border-t border-gray-700">
        <a 
          href="/" 
          target="_blank" 
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-gray-400 hover:text-white text-sm"
        >
          <ExternalLink className="w-4 h-4" />
          View Site
        </a>
      </div>
    </div>
  );

  // Render pages list
  const renderPagesList = () => (
    <div className="flex-1 bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b p-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Pages</h1>
          <p className="text-sm text-muted-foreground">Create and manage your website pages</p>
        </div>
        <Button onClick={() => setShowNewPageDialog(true)} className="gap-2">
          <Plus className="w-4 h-4" />
          Add New Page
        </Button>
      </div>

      {/* Pages Grid */}
      <div className="p-6">
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading pages...</div>
        ) : pages.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-medium mb-2">No pages yet</h3>
            <p className="text-muted-foreground mb-4">Create your first page to get started</p>
            <Button onClick={() => setShowNewPageDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Page
            </Button>
          </div>
        ) : (
          <div className="grid gap-4">
            {pages.map(page => (
              <div 
                key={page.slug}
                className="bg-white dark:bg-gray-800 rounded-xl border p-4 flex items-center gap-4 hover:shadow-md transition-shadow"
              >
                {/* Page Icon */}
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  {page.slug === 'home' ? (
                    <Home className="w-6 h-6 text-primary" />
                  ) : (
                    <FileText className="w-6 h-6 text-primary" />
                  )}
                </div>

                {/* Page Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium">{page.title}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      page.status === 'published' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {page.status}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <Globe className="w-3 h-3" />
                    /{page.slug}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setEditingPage(page)}
                  >
                    <Edit2 className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  
                  <a 
                    href={`/${page.slug === 'home' ? '' : page.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button variant="ghost" size="sm">
                      <Eye className="w-4 h-4" />
                    </Button>
                  </a>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => duplicatePage(page)}>
                        <Copy className="w-4 h-4 mr-2" />
                        Duplicate
                      </DropdownMenuItem>
                      {!['home', 'about', 'contact', 'menu', 'loyalty'].includes(page.slug) && (
                        <DropdownMenuItem 
                          onClick={() => deletePage(page.slug)}
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* New Page Dialog */}
      <Dialog open={showNewPageDialog} onOpenChange={setShowNewPageDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Page</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Page Title</Label>
              <Input
                value={newPageTitle}
                onChange={(e) => {
                  setNewPageTitle(e.target.value);
                  if (!newPageSlug) {
                    setNewPageSlug(e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''));
                  }
                }}
                placeholder="e.g., Catering Services"
              />
            </div>
            <div className="space-y-2">
              <Label>URL Slug</Label>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground text-sm">bulaccinocafe.com/</span>
                <Input
                  value={newPageSlug}
                  onChange={(e) => setNewPageSlug(e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''))}
                  placeholder="catering"
                  className="flex-1"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewPageDialog(false)}>
              Cancel
            </Button>
            <Button onClick={createPage}>
              Create Page
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );

  // Render page editor
  const renderPageEditor = () => (
    <PageEditor 
      page={editingPage} 
      onBack={() => setEditingPage(null)}
      onSave={(updatedPage) => {
        setEditingPage(updatedPage);
        fetchPages();
      }}
    />
  );

  return (
    <div className="flex h-[calc(100vh-64px)]" data-testid="simple-cms">
      {renderSidebar()}
      
      <div className="flex-1 overflow-auto">
        {activeSection === 'pages' && !editingPage && renderPagesList()}
        {activeSection === 'pages' && editingPage && renderPageEditor()}
        {activeSection === 'navigation' && <NavigationManager />}
        {activeSection === 'media' && <SimpleMediaLibrary />}
      </div>
    </div>
  );
}

// Page Editor Component
function PageEditor({ page, onBack, onSave }) {
  const [title, setTitle] = useState(page?.title || '');
  const [slug, setSlug] = useState(page?.slug || '');
  const [content, setContent] = useState('');
  const [featuredImage, setFeaturedImage] = useState(page?.featured_image || '');
  const [status, setStatus] = useState(page?.status || 'draft');
  const [seoTitle, setSeoTitle] = useState(page?.seo?.title || '');
  const [seoDescription, setSeoDescription] = useState(page?.seo?.description || '');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [activeTab, setActiveTab] = useState('content');
  const [showPreview, setShowPreview] = useState(false);
  const [previewDevice, setPreviewDevice] = useState('desktop');

  // Fetch full page data when editor opens
  useEffect(() => {
    const fetchFullPage = async () => {
      if (!page?.slug) return;
      setLoading(true);
      try {
        const res = await api.get(`/admin/cms/simple/pages/${page.slug}`);
        const fullPage = res.data;
        setTitle(fullPage.title || '');
        setContent(fullPage.content || '');
        setFeaturedImage(fullPage.featured_image || '');
        setStatus(fullPage.status || 'draft');
        setSeoTitle(fullPage.seo?.title || '');
        setSeoDescription(fullPage.seo?.description || '');
      } catch (err) {
        console.error('Failed to fetch page:', err);
      }
      setLoading(false);
    };
    fetchFullPage();
  }, [page?.slug]);

  const savePage = async (newStatus = status) => {
    setSaving(true);
    try {
      const res = await api.put(`/admin/cms/simple/pages/${page.slug}`, {
        title,
        slug,
        content,
        featured_image: featuredImage,
        status: newStatus,
        seo: {
          title: seoTitle,
          description: seoDescription
        }
      });
      
      toast.success(newStatus === 'published' ? 'Page published!' : 'Page saved!');
      setStatus(newStatus);
      onSave(res.data);
    } catch (err) {
      toast.error('Failed to save page');
    }
    setSaving(false);
  };

  return (
    <div className="flex-1 bg-gray-50 dark:bg-gray-900 flex flex-col h-full">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b p-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <div>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="text-xl font-semibold border-0 p-0 h-auto focus-visible:ring-0 bg-transparent"
              placeholder="Page Title"
            />
            <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
              <Globe className="w-3 h-3" />
              bulaccinocafe.com/{slug}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowPreview(true)}
            data-testid="preview-mode-btn"
          >
            <Eye className="w-4 h-4 mr-1" />
            Preview
          </Button>
          
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => savePage('draft')}
            disabled={saving}
          >
            Save Draft
          </Button>
          
          <Button 
            size="sm"
            onClick={() => savePage('published')}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Publish'}
          </Button>
        </div>
      </div>

      {/* Editor Tabs */}
      <div className="bg-white dark:bg-gray-800 border-b px-4">
        <div className="flex gap-4">
          {['content', 'settings', 'seo'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-3 px-2 border-b-2 text-sm font-medium transition-colors ${
                activeTab === tab 
                  ? 'border-primary text-primary' 
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Editor Content */}
      <div className="flex-1 overflow-auto p-6">
        {loading ? (
          <div className="max-w-4xl mx-auto flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading page content...</p>
            </div>
          </div>
        ) : activeTab === 'content' ? (
          <div className="max-w-4xl mx-auto">
            <WYSIWYGEditor 
              content={content} 
              onChange={setContent}
              onImageRequest={() => setShowMediaPicker(true)}
            />
          </div>
        ) : null}

        {!loading && activeTab === 'settings' && (
          <div className="max-w-2xl mx-auto space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl border p-6 space-y-4">
              <h3 className="font-medium">Page Settings</h3>
              
              <div className="space-y-2">
                <Label>URL Slug</Label>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground text-sm">bulaccinocafe.com/</span>
                  <Input
                    value={slug}
                    onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''))}
                    className="flex-1"
                    disabled={['home', 'about', 'contact', 'menu', 'loyalty'].includes(page.slug)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Featured Image</Label>
                {featuredImage ? (
                  <div className="relative">
                    <img 
                      src={featuredImage} 
                      alt="Featured" 
                      className="w-full h-48 object-cover rounded-lg"
                    />
                    <Button
                      variant="destructive"
                      size="sm"
                      className="absolute top-2 right-2"
                      onClick={() => setFeaturedImage('')}
                    >
                      Remove
                    </Button>
                  </div>
                ) : (
                  <Button 
                    variant="outline" 
                    onClick={() => setShowMediaPicker(true)}
                    className="w-full h-32 border-dashed"
                  >
                    <Image className="w-6 h-6 mr-2" />
                    Add Featured Image
                  </Button>
                )}
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Page Status</Label>
                  <p className="text-sm text-muted-foreground">
                    {status === 'published' ? 'This page is live' : 'This page is a draft'}
                  </p>
                </div>
                <Switch
                  checked={status === 'published'}
                  onCheckedChange={(checked) => setStatus(checked ? 'published' : 'draft')}
                />
              </div>
            </div>
          </div>
        )}

        {!loading && activeTab === 'seo' && (
          <div className="max-w-2xl mx-auto space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl border p-6 space-y-4">
              <h3 className="font-medium">SEO Settings</h3>
              
              <div className="space-y-2">
                <Label>SEO Title</Label>
                <Input
                  value={seoTitle}
                  onChange={(e) => setSeoTitle(e.target.value)}
                  placeholder={title || 'Page title'}
                />
                <p className="text-xs text-muted-foreground">
                  {(seoTitle || title || '').length}/60 characters
                </p>
              </div>

              <div className="space-y-2">
                <Label>Meta Description</Label>
                <textarea
                  value={seoDescription}
                  onChange={(e) => setSeoDescription(e.target.value)}
                  placeholder="Brief description of this page..."
                  className="w-full h-24 p-3 border rounded-lg text-sm resize-none"
                />
                <p className="text-xs text-muted-foreground">
                  {seoDescription.length}/160 characters
                </p>
              </div>

              {/* Google Preview */}
              <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                <p className="text-xs text-muted-foreground mb-2">Google Preview</p>
                <div>
                  <p className="text-blue-600 text-lg hover:underline cursor-pointer">
                    {seoTitle || title || 'Page Title'}
                  </p>
                  <p className="text-green-700 text-sm">
                    bulaccinocafe.com/{slug}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    {seoDescription || 'Add a meta description to improve SEO...'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Live Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 z-50 bg-gray-900/95 flex flex-col" data-testid="preview-modal">
          {/* Preview Header */}
          <div className="bg-gray-800 border-b border-gray-700 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h3 className="text-white font-medium flex items-center gap-2">
                <Eye className="w-4 h-4" />
                Preview Mode
              </h3>
              <span className="text-yellow-400 text-sm bg-yellow-400/10 px-2 py-1 rounded">
                Unsaved changes
              </span>
            </div>
            
            {/* Device Switcher */}
            <div className="flex items-center gap-2 bg-gray-700/50 rounded-lg p-1">
              <button
                onClick={() => setPreviewDevice('desktop')}
                className={`p-2 rounded transition-colors ${
                  previewDevice === 'desktop' 
                    ? 'bg-primary text-white' 
                    : 'text-gray-400 hover:text-white'
                }`}
                title="Desktop"
                data-testid="preview-desktop-btn"
              >
                <Monitor className="w-5 h-5" />
              </button>
              <button
                onClick={() => setPreviewDevice('tablet')}
                className={`p-2 rounded transition-colors ${
                  previewDevice === 'tablet' 
                    ? 'bg-primary text-white' 
                    : 'text-gray-400 hover:text-white'
                }`}
                title="Tablet"
                data-testid="preview-tablet-btn"
              >
                <Tablet className="w-5 h-5" />
              </button>
              <button
                onClick={() => setPreviewDevice('mobile')}
                className={`p-2 rounded transition-colors ${
                  previewDevice === 'mobile' 
                    ? 'bg-primary text-white' 
                    : 'text-gray-400 hover:text-white'
                }`}
                title="Mobile"
                data-testid="preview-mobile-btn"
              >
                <Smartphone className="w-5 h-5" />
              </button>
            </div>

            <div className="flex items-center gap-3">
              <a 
                href={`/${slug === 'home' ? '' : slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white text-sm flex items-center gap-1"
              >
                <ExternalLink className="w-4 h-4" />
                Open Live Page
              </a>
              <button
                onClick={() => setShowPreview(false)}
                className="p-2 text-gray-400 hover:text-white transition-colors"
                data-testid="close-preview-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Preview Container */}
          <div className="flex-1 flex items-start justify-center p-6 overflow-auto">
            <div 
              className={`bg-white rounded-lg shadow-2xl overflow-hidden transition-all duration-300 ${
                previewDevice === 'desktop' 
                  ? 'w-full max-w-6xl' 
                  : previewDevice === 'tablet' 
                    ? 'w-[768px]' 
                    : 'w-[375px]'
              }`}
              style={{ minHeight: '600px' }}
            >
              {/* Browser Chrome */}
              <div className="bg-gray-100 border-b px-4 py-2 flex items-center gap-2">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-400"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                  <div className="w-3 h-3 rounded-full bg-green-400"></div>
                </div>
                <div className="flex-1 bg-white rounded px-3 py-1 text-sm text-gray-600 text-center mx-4">
                  bulaccinocafe.com/{slug === 'home' ? '' : slug}
                </div>
              </div>
              
              {/* Preview Content */}
              <div 
                className="preview-content p-8 min-h-[500px]"
                style={{
                  fontFamily: "'Nunito', sans-serif",
                }}
              >
                {/* Page Title */}
                <h1 className="text-4xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {title || 'Page Title'}
                </h1>
                
                {/* Rendered HTML Content */}
                <div 
                  className="prose prose-lg max-w-none"
                  style={{
                    '--tw-prose-headings': '#1a1a1a',
                    '--tw-prose-body': '#374151',
                    '--tw-prose-links': '#4a7c59',
                  }}
                  dangerouslySetInnerHTML={{ 
                    __html: DOMPurify.sanitize(content || '<p class="text-gray-400 italic">No content yet. Start editing to see your preview.</p>') 
                  }}
                />
              </div>
            </div>
          </div>

          {/* Preview Footer */}
          <div className="bg-gray-800 border-t border-gray-700 px-4 py-3 flex items-center justify-center gap-4">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowPreview(false)}
              className="bg-transparent border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              Continue Editing
            </Button>
            <Button 
              size="sm"
              onClick={() => {
                savePage('published');
                setShowPreview(false);
              }}
              disabled={saving}
            >
              {saving ? 'Publishing...' : 'Publish Changes'}
            </Button>
          </div>
        </div>
      )}

      {/* Media Picker Dialog */}
      <Dialog open={showMediaPicker} onOpenChange={setShowMediaPicker}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Select Image</DialogTitle>
          </DialogHeader>
          <SimpleMediaLibrary 
            selectionMode={true}
            onSelect={(url) => {
              if (activeTab === 'settings') {
                setFeaturedImage(url);
              }
              setShowMediaPicker(false);
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
